import { API } from '@/config';
import { AuthApiError } from '@/features/auth/api';

export type WatchedPage = {
  page_id: string;
  slug?: string;
  title?: string;
  language?: string;
  created_at: string;
};

export type WatchedPagesResponse = {
  watches: WatchedPage[];
};

type ApiEnvelope<T> = {
  ok?: boolean;
  data?: T;
  error?: unknown;
};

export type WatchApiClient = {
  followPage(pageID: string): Promise<void>;
  unfollowPage(pageID: string): Promise<void>;
  listWatches(): Promise<WatchedPagesResponse>;
};

export function createWatchApiClient(
  getToken: () => string | null,
  baseURL = API.wikiBase,
): WatchApiClient {
  const normalizedBaseURL = baseURL.replace(/\/+$/, '');

  return {
    followPage(pageID) {
      const token = requireToken(getToken());

      return request<void>(`${normalizedBaseURL}/watches`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ page_id: pageID }),
      });
    },
    unfollowPage(pageID) {
      const token = requireToken(getToken());

      return request<void>(`${normalizedBaseURL}/watches/${encodeURIComponent(pageID)}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
    listWatches() {
      const token = requireToken(getToken());

      return request<WatchedPagesResponse>(`${normalizedBaseURL}/watches`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
  };
}

function requireToken(token: string | null): string {
  if (!token) {
    throw new AuthApiError('error_login_required', 401);
  }

  return token;
}

async function request<T>(input: string, init: RequestInit): Promise<T> {
  const response = await fetch(input, init);
  const body = await parseBody(response);

  if (!response.ok) {
    throw new AuthApiError(errorMessage(body, response.statusText), response.status);
  }

  if (isEnvelope<T>(body)) {
    if (body.ok === false) {
      throw new AuthApiError(errorMessage(body, response.statusText), response.status);
    }

    if ('data' in body) {
      return body.data as T;
    }
  }

  return body as T;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isEnvelope<T>(body: unknown): body is ApiEnvelope<T> {
  return Boolean(body && typeof body === 'object' && 'ok' in body);
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: unknown }).error;

    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;

      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }

  return fallback || 'request failed';
}
